import { Link } from '@tanstack/react-router';
import { Building2, Info } from 'lucide-react';
import { EXTERNAL_SYSTEM_LABELS, labelOf, PARTY_SOURCE_LABELS } from '@blimo/shared';
import { Alert, AlertDescription, Badge, Skeleton } from '@blimo/ui';

/** Link to a party detail page. @param {{ party: { id: number|string, display_name?: string|null } | null | undefined }} props */
export function PartyLink({ party }) {
  if (!party) return <span className="text-muted-foreground">–</span>;
  return (
    <Link to="/personen/$partyId" params={{ partyId: String(party.id) }} className="font-medium text-primary hover:underline">
      {party.display_name || `#${party.id}`}
    </Link>
  );
}

/**
 * Parties of an ownership/tenancy, each with its role label.
 * @param {{ parties?: Array<{ role: string, party: any }>, roleLabels: Record<string, string> }} props
 */
export function PartyList({ parties, roleLabels }) {
  if (!parties?.length) return <span className="text-muted-foreground">–</span>;
  return (
    <ul className="flex flex-col gap-0.5">
      {parties.map((entry) => (
        <li key={`${entry.role}-${entry.party?.id}`} className="flex flex-wrap items-center gap-1.5">
          <PartyLink party={entry.party} />
          {entry.role && entry.role !== 'primary' && entry.role !== 'main' && (
            <span className="text-xs text-muted-foreground">({labelOf(roleLabels, entry.role)})</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Where a record is maintained. @param {{ source?: string|null }} props */
export function SourceBadge({ source }) {
  if (!source) return null;
  return <Badge variant={source === 'manual' ? 'secondary' : 'info'}>Quelle: {labelOf(PARTY_SOURCE_LABELS, source)}</Badge>;
}

/** External keys (Cobra-ID, ONR …). @param {{ references?: Array<{ system: string, key_type: string, value: string }> }} props */
export function ExternalReferences({ references }) {
  if (!references?.length) return <p className="text-sm text-muted-foreground">Keine externen Schlüssel.</p>;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {references.map((ref) => (
        <li key={`${ref.system}-${ref.key_type}-${ref.value}`}>
          <Badge variant="outline" className="font-normal">
            <span className="text-muted-foreground">
              {labelOf(EXTERNAL_SYSTEM_LABELS, ref.system)} · {ref.key_type}
            </span>
            <span className="numeric font-medium">{ref.value}</span>
          </Badge>
        </li>
      ))}
    </ul>
  );
}

/** Read-only hint for fields maintained in a source system. @param {{ children: import('react').ReactNode }} props */
export function ReadOnlyHint({ children }) {
  return (
    <Alert variant="info">
      <Info aria-hidden="true" />
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}

/** Skeleton for detail pages while loading. */
export function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Wird geladen">
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-4 w-40" />
      <div className="grid gap-3 lg:grid-cols-2">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  );
}

/** Small "Unit 12 · Wohnung 3. OG" label. @param {{ unit?: { id: number, number?: string|null, designation?: string|null } | null, link?: boolean }} props */
export function UnitLabel({ unit, link = true }) {
  if (!unit) return <span className="text-muted-foreground">–</span>;
  const text = [unit.number, unit.designation].filter(Boolean).join(' · ') || `#${unit.id}`;
  if (!link) return <span>{text}</span>;
  return (
    <Link to="/einheiten/$unitId" params={{ unitId: String(unit.id) }} className="font-medium text-primary hover:underline">
      {text}
    </Link>
  );
}

/** Property link. @param {{ property?: { id: number, name?: string|null, number?: string|null } | null }} props */
export function PropertyLink({ property }) {
  if (!property) return <span className="text-muted-foreground">–</span>;
  return (
    <Link to="/liegenschaften/$propertyId" params={{ propertyId: String(property.id) }} className="inline-flex items-center gap-1 text-primary hover:underline">
      <Building2 className="size-3.5" aria-hidden="true" />
      {property.name || `#${property.id}`}
      {property.number && <span className="numeric text-muted-foreground">({property.number})</span>}
    </Link>
  );
}
